/**
 * App-level hook for synchronizing feature flags in real-time.
 *
 * Mount this hook at the application root to enable automatic flag updates
 * across all components that use feature flags.
 *
 * @see specs/feature-flags-system.md
 */
import { useCallback, useState } from "react"
import { useFlagStream, type FlagUpdateEvent } from "./useFlagStream"

export type UseFeatureFlagSyncOptions = {
  /** Whether to enable real-time sync (default: true) */
  enabled?: boolean
  /** Called when flags are updated */
  onFlagUpdate?: (event: FlagUpdateEvent) => void
  /** Called on connection error */
  onError?: (error: Event) => void
}

export type UseFeatureFlagSyncReturn = {
  /** Whether the SSE stream is currently connected */
  isConnected: boolean
  /** The current environment being synced */
  environment: string | null
  /** Last sync timestamp */
  lastSync: Date | null
  /** Number of updates received this session */
  updateCount: number
  /** Manually trigger a reconnection */
  reconnect: () => void
}

/**
 * Synchronize feature flags in real-time across the application.
 *
 * This hook should be mounted once at the application root level
 * (e.g., in __root.tsx or a top-level layout component).
 *
 * @example
 * ```tsx
 * // In __root.tsx or layout
 * function RootLayout() {
 *   const { isConnected, environment } = useFeatureFlagSync()
 *
 *   return (
 *     <>
 *       {!isConnected && <div>Reconnecting to feature flag service...</div>}
 *       <Outlet />
 *     </>
 *   )
 * }
 * ```
 */
export function useFeatureFlagSync(
  options: UseFeatureFlagSyncOptions = {},
): UseFeatureFlagSyncReturn {
  const { enabled = true, onFlagUpdate, onError } = options

  const [environment, setEnvironment] = useState<string | null>(null)
  const [lastSync, setLastSync] = useState<Date | null>(null)
  const [updateCount, setUpdateCount] = useState(0)

  const handleConnected = useCallback(
    (data: { environment: string; userId: string; timestamp: string }) => {
      setEnvironment(data.environment)
      setLastSync(new Date(data.timestamp))
    },
    [],
  )

  const handleFlagUpdate = useCallback(
    (event: FlagUpdateEvent) => {
      setLastSync(new Date(event.timestamp))
      setUpdateCount((prev) => prev + 1)
      onFlagUpdate?.(event)
    },
    [onFlagUpdate],
  )

  const { isConnected, reconnect } = useFlagStream({
    enabled,
    onConnected: handleConnected,
    onFlagUpdate: handleFlagUpdate,
    onError,
    autoReconnect: true,
  })

  return {
    isConnected,
    environment,
    lastSync,
    updateCount,
    reconnect,
  }
}
